import { Dispatch, SetStateAction, Suspense, useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Loader } from '@givebutter/primitives/loader';
import * as Popover from '@givebutter/primitives/popover';
import * as Portal from '@radix-ui/react-portal';
import { IEvents as TinyMCEEvents } from '@tinymce/tinymce-react/lib/cjs/main/ts/Events';
import {
    SEARCH_TRIGGER_DATA_ATTRIBUTE,
    TASK_DESCRIPTION_EDITOR_BODY_CLASS,
} from 'components/tasks/constants';
import { LinkPicker } from 'components/tasks/form/links/LinkPicker';
import { LinkPopoverContent } from 'components/tasks/form/links/LinkPopoverContent';
import { LinkedResourcePopoverItem } from 'components/tasks/types';
import { isLinkNode, isSearchTrigger } from 'components/tasks/utils/editor-search-helpers';
import { TINY_LINKED_RESOURCE_TAG_CLASSES } from 'components/tasks/utils/generateTaggedResourceHtml';
import { useLinkSearchStore } from 'components/tasks/utils/useLinkSearchStore';
import * as Wysiwyg from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { isTagCommand } from 'helpers/regex';
import { Editor, EditorEvent } from 'tinymce';

const SEARCH_TRIGGER_ID = 'gb-search-trigger';
const HOVER_CARD_OPEN_DELAY = 250;
const LINK_NODE_ALLOWED_KEYS = [
    'Backspace',
    'Escape',
    'ArrowDown',
    'ArrowRight',
    'ArrowUp',
    'ArrowLeft',
];

interface TaskDescriptionEditorProps {
    editorInstance: Editor | null;
    inModal?: boolean;
    setEditorInstance: Dispatch<SetStateAction<Editor | null>>;
}
const TaskDescriptionEditor = ({
    editorInstance,
    setEditorInstance,
    inModal = false,
}: TaskDescriptionEditorProps) => {
    const [resource, setResource] = useState<LinkedResourcePopoverItem | null>(null);
    const [popoverAnchorStyle, setPopoverAnchorStyle] = useState<{ top: number; left: number }>();
    const [isOpen, setIsOpen] = useState(false);
    const isLinkHovered = useRef(false);
    const { listRef, setQuery } = useLinkSearchStore(({ listRef, setQuery }) => ({
        listRef,
        setQuery,
    }));

    const openSearchPopover = useCallback(
        (editor: Editor) => {
            const nodePosition = editor.selection.getNode().getBoundingClientRect();
            const textareaTop = nodePosition.top + nodePosition.height;
            const textareaLeft = nodePosition.left;

            setPopoverAnchorStyle({ left: textareaLeft, top: textareaTop });

            flushSync(() => setIsOpen(true));
        },
        [setIsOpen],
    );

    const closeSearchPopover = useCallback(() => {
        setIsOpen(false);
        setQuery('');
    }, [setIsOpen, setQuery]);

    const handleOnNodeChange: TinyMCEEvents['onNodeChange'] = useCallback(
        (event, editor) => {
            if (isSearchTrigger(event.element as HTMLElement)) {
                openSearchPopover(editor);
            } else {
                closeSearchPopover();
                editor.contentDocument
                    .querySelectorAll(`span[${SEARCH_TRIGGER_DATA_ATTRIBUTE}]`)
                    .forEach(t => t.remove());
            }
        },
        [openSearchPopover, closeSearchPopover],
    );

    const handleOnKeyDown: TinyMCEEvents['onKeyDown'] = useCallback(
        (event, editor) => {
            const currentNode = editor.selection.getNode();
            if (event.key === 'ArrowDown' && isSearchTrigger(currentNode)) {
                const firstFoundLi = listRef.current?.querySelector('[role="option"]');
                if (firstFoundLi) (firstFoundLi as HTMLElement).focus();
            }

            if (isLinkNode(currentNode)) {
                if (!LINK_NODE_ALLOWED_KEYS.includes(event.key)) {
                    event.preventDefault();
                }
            }
        },
        [listRef],
    );

    const handleOnKeyUp: TinyMCEEvents['onKeyUp'] = useCallback(
        (event, editor) => {
            const currentNode = editor.selection.getNode();

            maybeRemoveNode({ currentNode, event });

            if (isSearchTrigger(currentNode)) {
                if (currentNode.textContent?.includes('@')) {
                    setQuery(currentNode.textContent ?? '');
                }
            }
            maybeInsertTriggerTag({ currentNode, editor, onOpen: () => openSearchPopover(editor) });
        },
        [openSearchPopover, setQuery],
    );

    const handleOnMouseOver: TinyMCEEvents['onMouseOver'] = useCallback(event => {
        const { target } = event;
        if (isLinkNode(target)) {
            isLinkHovered.current = true;
            setTimeout(() => {
                if (isLinkHovered.current) {
                    setResource({
                        type: target.dataset.gbLinkType,
                        id: target.dataset.gbLinkId,
                        node: target,
                    });
                }
            }, HOVER_CARD_OPEN_DELAY);
        } else {
            setResource(null);
        }
    }, []);

    const { width, left, top } = resource?.node.getBoundingClientRect() || {};

    return (
        <>
            <Wysiwyg.Root>
                <UseFormWysiwyg
                    name="description"
                    inline
                    init={editorInitOptions}
                    onInit={(_, editor) => {
                        editor.bodyElement?.classList.add(TASK_DESCRIPTION_EDITOR_BODY_CLASS);
                        setEditorInstance(editor);
                    }}
                    onKeyUp={handleOnKeyUp}
                    onKeyDown={handleOnKeyDown}
                    onNodeChange={handleOnNodeChange}
                    onMouseOver={handleOnMouseOver}
                />
            </Wysiwyg.Root>

            <Popover.Root open={!!resource && !inModal}>
                <Portal.Root>
                    <Popover.Anchor style={{ position: 'fixed', left, top, width }} />
                </Portal.Root>
                <Popover.Portal>
                    <Popover.Content
                        side="bottom"
                        align="center"
                        sideOffset={20}
                        onInteractOutside={() => {
                            isLinkHovered.current = false;
                            setResource(null);
                        }}
                        onMouseLeave={() => {
                            isLinkHovered.current = false;
                            setResource(null);
                        }}
                    >
                        <Suspense fallback={<Loader />}>
                            <LinkPopoverContent resource={resource} />
                        </Suspense>
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>

            <LinkPicker
                open={isOpen}
                onOpenChange={setIsOpen}
                editorInstance={editorInstance}
                onSelectItem={() => setIsOpen(false)}
            >
                <Portal.Root asChild>
                    <div style={{ position: 'fixed', ...popoverAnchorStyle }} />
                </Portal.Root>
            </LinkPicker>
        </>
    );
};

interface MaybeRemoveNodeArgs {
    currentNode: HTMLElement;
    event: EditorEvent<KeyboardEvent>;
}
const maybeRemoveNode = ({ currentNode, event }: MaybeRemoveNodeArgs) => {
    if (event.key === 'Backspace') {
        if (
            (!isTagCommand.test(currentNode.textContent ?? '') && isSearchTrigger(currentNode)) ||
            isLinkNode(currentNode)
        ) {
            currentNode.remove();
        }
    }

    if (event.key === 'Escape' && isSearchTrigger(currentNode)) {
        currentNode.remove();
    }
};

interface MaybeInsertTriggerTag {
    currentNode: HTMLElement;
    editor: Editor;
    onOpen: () => void;
}
const maybeInsertTriggerTag = ({ currentNode, editor, onOpen }: MaybeInsertTriggerTag) => {
    const triggerIndex = currentNode.innerText.indexOf('@');
    const previousChar = currentNode.innerText[triggerIndex - 1];
    const hasTriggerKey = triggerIndex >= 0;
    const hasTriggerSpan =
        currentNode.id === SEARCH_TRIGGER_ID ||
        currentNode.firstElementChild?.id === SEARCH_TRIGGER_ID;

    if (
        (typeof previousChar !== 'undefined' && previousChar !== ' ') ||
        !hasTriggerKey ||
        hasTriggerSpan
    )
        return;

    editor.dom.setHTML(
        currentNode,
        currentNode.innerHTML.replace(
            '@',
            `<span id="${SEARCH_TRIGGER_ID}" class="${TINY_LINKED_RESOURCE_TAG_CLASSES}" ${SEARCH_TRIGGER_DATA_ATTRIBUTE}>@</span>`,
        ),
    );
    const triggerNode = editor.dom.get(SEARCH_TRIGGER_ID);
    if (triggerNode) {
        triggerNode.focus();
        editor.selection.setCursorLocation(triggerNode, triggerNode.textContent?.length || 0);
        onOpen();
    }
};

const editorInitOptions: Wysiwyg.WysiwygContentEditorProps['init'] = {
    statusbar: false,
    toolbar: false,
    toolbar_persist: false,
    content_style: `
        ${Wysiwyg.Constants.APP_FONT}
        .${TASK_DESCRIPTION_EDITOR_BODY_CLASS}.mce-content-body { 
            line-height: 1.75;
            min-height: 1.75rem;
            cursor: text;
        }
        .${TASK_DESCRIPTION_EDITOR_BODY_CLASS}.mce-content-body[data-mce-placeholder]::before {
            width: unset;
        }
        .${TASK_DESCRIPTION_EDITOR_BODY_CLASS}.mce-content-body.mce-edit-focus::before {
            font-family: unset;
            line-height: unset;
        }
        .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
            color: #6b7280 !important;
        }
    `,
    link_quicklink: true,
    menubar: false,
    menu: undefined,
    plugins: ['quickbars', 'autolink', 'link'],
    quickbars_image_toolbar: false,
    quickbars_insert_toolbar: false,
    quickbars_selection_toolbar: 'bold italic underline strikethrough',
    placeholder: 'Add details about your task here or type ‘@’ to link a resource',
    extended_valid_elements: 'svg[*],path[*]',
};

export { TaskDescriptionEditor, editorInitOptions };
